import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, Volume2, VolumeX, AlertCircle } from "lucide-react";
import { Button } from "../../ui/button";
import { Slider } from "../../ui/slider";
import { Alert, AlertDescription } from "../../ui/alert";
import { Card } from "@/components/ui/card";

export default function TrackPlayer({ src }) {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(null);
  const [volume, setVolume] = useState(1.0);
  const [muted, setMuted] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const [loading, setLoading] = useState(false);

  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsReady(true);
      setError(null);
      setLoading(false);
    };

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    const onWaiting = () => {
      setLoading(true);
    };

    const onCanPlay = () => {
      setLoading(false);
    };

    const onError = (e) => {
      console.error("Audio Error:", e);
      // Check if it's a 403 or similar
      if (audio.error) {
        console.error("Media Error Code:", audio.error.code);
        console.error("Media Error Message:", audio.error.message);
      }
      setError("Failed to load audio. It might be unavailable or restricted.");
      setPlaying(false);
      setLoading(false);
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("error", onError);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          console.error("Play failed:", e);
          setPlaying(false);
        });
      }
    } else {
      audio.pause();
    }
  }, [playing]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const togglePlay = () => {
    setPlaying(!playing);
  };

  const handleSeekChange = (value) => {
    const newTime = value[0];
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const toggleMute = () => {
    setMuted(!muted);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (error) {
    return (
      <Card variant="destructive" className="py-2 px-2 flex">
        <AlertCircle className="h-4 w-4" />
        <p className="ml-2 text-xs">{error}</p>
      </Card>
    );
  }

  return (
    <div className="flex items-center gap-3 w-full bg-secondary/20 p-2 rounded-md border">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        crossOrigin="anonymous"
      />

      <Button
        variant="ghost"
        size="icon"
        onClick={togglePlay}
        disabled={!isReady}
        className="h-8 w-8 shrink-0"
      >
        {loading || !isReady ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        ) : playing ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4" />
        )}
      </Button>

      <div className="flex-1 flex flex-col gap-1">
        <Slider
          value={[currentTime]}
          max={duration || 100}
          step={0.1}
          onValueChange={handleSeekChange}
          className="cursor-pointer"
          disabled={!isReady}
        />
        <div className="flex justify-between text-[10px] text-muted-foreground px-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleMute}
          className="h-6 w-6"
        >
          {muted || volume === 0 ? (
            <VolumeX className="h-3 w-3" />
          ) : (
            <Volume2 className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  );
}
